/**
 * opencv.js 필터 워커와 이야기하는 쪽.
 *
 * 예전에는 opencv.js 를 메인 스레드에 붙였다. 7.9MB Emscripten 빌드라 WASM 컴파일이
 * 도는 동안 화면이 통째로 멈췄고, 단말기 상세 모달을 열 때 그게 그대로 드러났다.
 * 라이브러리를 걷어내는 대신 워커로 옮긴 이유는 public/js/opencvFilter.worker.js 에 적었다.
 *
 * 워커는 처음 필요할 때 한 번만 만들고, 이후 요청은 같은 워커가 처리한다.
 */

export type ImageFilterValues = {
  blur: number
  sharpening: number
  contrast: number
  brightness: number
  gamma: number
}

const WORKER_SRC = '/js/opencvFilter.worker.js'

type Pending = {
  resolve: (value: ImageData) => void
  reject: (error: Error) => void
}

let worker: Worker | null = null
let ready: Promise<void> | null = null
let lastId = 0
const pending = new Map<number, Pending>()

/* 워커가 죽으면 기다리던 요청은 영영 응답을 못 받는다. 전부 깨우고 다음 호출이
   새 워커를 만들 수 있게 상태를 비운다. */
const failAll = (error: Error) => {
  pending.forEach((entry) => entry.reject(error))
  pending.clear()
  worker = null
  ready = null
}

const ensureWorker = (): Promise<void> => {
  if (ready) return ready

  ready = new Promise<void>((resolve, reject) => {
    let instance: Worker
    try {
      instance = new Worker(WORKER_SRC)
    } catch (error) {
      reject(new Error('필터 워커를 만들지 못했습니다'))
      ready = null
      return
    }
    worker = instance

    instance.onmessage = (event: MessageEvent) => {
      const message = event.data || {}

      if (message.type === 'ready') {
        resolve()
        return
      }

      if (message.type === 'fatal') {
        const error = new Error(message.message || 'opencv.js 초기화 실패')
        reject(error)
        failAll(error)
        instance.terminate()
        return
      }

      const entry = pending.get(message.id)
      if (!entry) return
      pending.delete(message.id)

      if (message.type === 'result') {
        entry.resolve(
          new ImageData(new Uint8ClampedArray(message.pixels), message.width, message.height)
        )
      } else {
        entry.reject(new Error(message.message || '필터 적용 실패'))
      }
    }

    instance.onerror = () => {
      const error = new Error('필터 워커 오류')
      reject(error)
      failAll(error)
    }

    instance.postMessage({type: 'warmup'})
  })

  return ready
}

/**
 * 모달이 열리기 전에 워커를 띄워 opencv.js 를 미리 컴파일해둔다.
 * 워커에서 도는 일이라 목록 화면은 그동안에도 멈추지 않는다.
 */
export const warmUpOpenCvFilter = (): Promise<void> => ensureWorker()

/**
 * 한가해질 때까지 기다렸다가 워커를 띄운다.
 *
 * 컴파일은 워커가 하니 화면이 멈추지는 않지만, 7.9MB 다운로드가 목록 조회와 겹치면
 * 느린 회선에서 목록이 늦게 뜬다. 시작만 뒤로 미루면 그 경합이 없어진다.
 *
 * 돌려주는 함수를 부르면 아직 시작하지 않은 예약을 취소한다.
 */
export const scheduleOpenCvFilterWarmUp = (onReady: () => void, onError: (e: any) => void) => {
  let cancelled = false

  const start = () => {
    if (cancelled) return
    warmUpOpenCvFilter()
      .then(() => {
        if (!cancelled) onReady()
      })
      .catch((error) => {
        if (!cancelled) onError(error)
      })
  }

  /* Safari 에는 requestIdleCallback 이 없어서 약간 늦춘 타이머로 대신한다 */
  const ric = (window as any).requestIdleCallback
  const handle =
    typeof ric === 'function' ? ric(start, {timeout: 3000}) : window.setTimeout(start, 1500)

  return () => {
    cancelled = true
    const cancelIdle = (window as any).cancelIdleCallback
    if (typeof ric === 'function') {
      if (typeof cancelIdle === 'function') cancelIdle(handle)
    } else {
      window.clearTimeout(handle)
    }
  }
}

/**
 * 필터를 적용한 ImageData 를 돌려준다.
 *
 * 넘긴 imageData 의 버퍼는 워커로 옮겨가므로(transfer) 호출한 뒤에는 쓸 수 없다.
 * 캔버스에서 getImageData 로 갓 꺼낸 것을 넘기면 된다.
 */
export const runOpenCvFilter = async (
  imageData: ImageData,
  filter: ImageFilterValues
): Promise<ImageData> => {
  await ensureWorker()
  const instance = worker
  if (!instance) throw new Error('필터 워커가 없습니다')

  const id = ++lastId
  const buffer = imageData.data.buffer

  return new Promise<ImageData>((resolve, reject) => {
    pending.set(id, {resolve, reject})
    instance.postMessage(
      {
        type: 'filter',
        id,
        pixels: buffer,
        width: imageData.width,
        height: imageData.height,
        filter,
      },
      [buffer]
    )
  })
}
